import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { RegionProvider } from "@/contexts/RegionContext";
import { RequireAuth } from "@/components/auth/RequireAuth";

// Phase 2G · <Landing /> preserved at /app/frontend/src/pages/Landing.jsx.
// It is NOT imported here on purpose while the product is private.
// To restore: re-add `import Landing from "@/pages/Landing";` and swap
// the "/" route element back to <Landing />, then delete PublicHome.jsx.
import PublicHome from "@/pages/PublicHome";
import Demo from "@/pages/Demo";
import Pricing from "@/pages/Pricing";
import { Login, Signup } from "@/pages/auth/AuthScreen";

import AppShell from "@/pages/app/AppShell";
import Dashboard from "@/pages/app/Dashboard";
import { Projects, ProjectNew } from "@/pages/app/Projects";
import {
    ProjectWorkspace,
    SetupTab,
    ThermalTab,
    MaturityTab,
    MonitoringTab,
    CalibrationTab,
    YieldTab,
    ReportsTab,
} from "@/pages/app/ProjectWorkspace";
import { Materials, Calibrations, Account } from "@/pages/app/AccountAndLibrary";
import Billing from "@/pages/billing/Billing";
import BillingSuccess from "@/pages/billing/Success";
import BillingCancel from "@/pages/billing/Cancel";

function App() {
    return (
        <div className="App" data-testid="thermocalc-root">
            <BrowserRouter>
                <AuthProvider>
                    <RegionProvider>
                        <Routes>
                        {/* Phase 2G · public cover only.
                            Product-facing routes are auth-gated below; the
                            previous <Landing /> component is preserved on
                            disk for a clean swap-back. */}
                        <Route path="/" element={<PublicHome />} />
                        <Route path="/demo" element={<RequireAuth><Demo /></RequireAuth>} />
                        <Route path="/pricing" element={<RequireAuth><Pricing /></RequireAuth>} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />

                        {/* Billing — auth required */}
                        <Route path="/billing" element={<RequireAuth><Billing /></RequireAuth>} />
                        <Route path="/billing/success" element={<RequireAuth><BillingSuccess /></RequireAuth>} />
                        <Route path="/billing/cancel" element={<RequireAuth><BillingCancel /></RequireAuth>} />

                        {/* App (auth-gated) */}
                        <Route
                            path="/app"
                            element={
                                <RequireAuth>
                                    <AppShell />
                                </RequireAuth>
                            }
                        >
                            <Route index element={<Dashboard />} />
                            <Route path="projects" element={<Projects />} />
                            <Route path="projects/new" element={<ProjectNew />} />
                            <Route
                                path="projects/:id"
                                element={<ProjectWorkspace />}
                            >
                                <Route
                                    index
                                    element={<Navigate to="setup" replace />}
                                />
                                <Route path="setup" element={<SetupTab />} />
                                <Route path="thermal" element={<ThermalTab />} />
                                <Route path="maturity" element={<MaturityTab />} />
                                <Route
                                    path="monitoring"
                                    element={<MonitoringTab />}
                                />
                                <Route
                                    path="calibration"
                                    element={<CalibrationTab />}
                                />
                                <Route path="yield" element={<YieldTab />} />
                                <Route path="reports" element={<ReportsTab />} />
                            </Route>
                            <Route path="library/materials" element={<Materials />} />
                            <Route path="library/calibrations" element={<Calibrations />} />
                            <Route path="account" element={<Account />} />
                        </Route>

                        {/* Fallback */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </RegionProvider>
                </AuthProvider>
            </BrowserRouter>
            <Toaster
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#0e1416",
                        color: "#e6ebec",
                        border: "1px solid #1a2124",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "13px",
                    },
                }}
            />
        </div>
    );
}

export default App;
